import React, { useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useSWRConfig } from 'swr'
import { PrimaryButton } from 'shared/components/button'
import FormInput from 'shared/components/form-input'
import { BadRequest } from 'shared/errors/bad-request'
import { USER_DATA_API } from 'shared/hooks/use-user'
import { joinCommunity, login } from '../api/community-api'
import { useCommunityWithTopics } from '../hooks/use-community-with-topics'

interface UserLoginFormInterface {
  email: string
  password: string
}

const defaultErrors: UserLoginFormInterface = {
  email: '',
  password: '',
}

const defaultLoginData: UserLoginFormInterface = {
  email: '',
  password: '',
}

function LoginForm() {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const { cache } = useSWRConfig()
  const { communityPath } = router.query
  const { data: communityData } = useCommunityWithTopics()
  const [errors, setErrors] = useState(defaultErrors)
  const [commonError, setCommonError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [loginData, setLoginData] = useState(defaultLoginData)

  async function submit() {
    try {
      setIsLoading(true)
      await login(loginData.email, loginData.password)
      if (!communityData?.ownerRestricted) {
        await joinCommunity(communityPath as string)
      }
      cache.delete(USER_DATA_API)
      await router.replace(`/community/${communityPath}`)
      setIsLoading(false)
    } catch (e) {
      setIsLoading(false)
      if (e instanceof BadRequest) {
        if (e.errors.fields) {
          setErrors({
            email: e.errors.fields.email?.join('\n'),
            password: e.errors.fields.password?.join('\n'),
          })
        } else if (e.errors.common) {
          setCommonError(e.errors.common.join('\n'))
        }
      }
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <FormInput
        type="email"
        value={loginData.email}
        className="flex-auto"
        label={t('global.email_address')}
        onChange={e => {
          setLoginData(prevState => ({ ...prevState, email: e.target.value }))
        }}
        maxLength={180}
        error={errors.email}
      />
      <FormInput
        type="password"
        value={loginData.password}
        className="flex-auto"
        label={t('global.password')}
        onChange={e => {
          setLoginData(prevState => ({
            ...prevState,
            password: e.target.value,
          }))
        }}
        maxLength={180}
        error={errors.password}
      />
      <PrimaryButton
        isLoading={isLoading}
        onClick={submit}
        disabled={!loginData.email || !loginData.password || isLoading}
      >
        {t('join_form.button.login.title')}
      </PrimaryButton>
      {commonError && <p className="mt-2 text-center text-sm text-danger">{commonError}</p>}
    </div>
  )
}

export default LoginForm
